import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system/legacy';
import { STORAGE_KEYS, DEFAULT_REVIEW_SCHEDULE, SPACED_INTERVALS } from '../constants/categories';
import { generateId, sortByCreatedAt } from '../utils/helpers';
import { syncAnswer } from './syncService';

// ==================== QUESTÕES ====================

// Diretório permanente das imagens de questão (a URI do picker aponta pro cache, que o SO pode limpar)
const QUESTION_IMAGES_DIR = `${FileSystem.documentDirectory}question_images/`;

// Copia a imagem escolhida no picker para armazenamento permanente e retorna a URI definitiva.
// Imagem é enriquecimento — falha não pode impedir de salvar a questão (retorna null).
export const persistQuestionImage = async (sourceUri) => {
  if (!sourceUri) return null;
  if (sourceUri.startsWith(QUESTION_IMAGES_DIR)) return sourceUri; // já persistida (edição sem troca)
  try {
    await FileSystem.makeDirectoryAsync(QUESTION_IMAGES_DIR, { intermediates: true });
    const extension = sourceUri.includes('.') ? sourceUri.split('.').pop().toLowerCase() : 'jpg';
    const destUri = `${QUESTION_IMAGES_DIR}${generateId()}.${extension}`;
    await FileSystem.copyAsync({ from: sourceUri, to: destUri });
    return destUri;
  } catch (error) {
    return null;
  }
};

// Remove o arquivo de imagem de uma questão (só arquivos do nosso diretório)
export const deleteQuestionImage = async (imageUri) => {
  if (!imageUri || !imageUri.startsWith(QUESTION_IMAGES_DIR)) return;
  try {
    await FileSystem.deleteAsync(imageUri, { idempotent: true });
  } catch (error) {
    // arquivo órfão é aceitável; nunca bloquear o fluxo por isso
  }
};

// Salva todas as questões
export const saveQuestions = async (questions) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEYS.QUESTIONS, JSON.stringify(questions));
    return true;
  } catch (error) {

    return false;
  }
};

// Carrega todas as questões
export const loadQuestions = async () => {
  try {
    const data = await AsyncStorage.getItem(STORAGE_KEYS.QUESTIONS);
    if (data) {
      const questions = JSON.parse(data);
      // Ordena por ordem manual ou data de criação
      return questions.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return b.createdAt - a.createdAt;
      });
    }

    // Se não há questões salvas, carregar do seed (primeira instalação)
    const seedQuestions = await loadSeedQuestions();
    if (seedQuestions.length > 0) {
      await saveQuestions(seedQuestions);
      return seedQuestions;
    }

    return [];
  } catch (error) {

    return [];
  }
};

// Carrega questões do arquivo seed (embarcadas no app)
const loadSeedQuestions = async () => {
  try {
    const seedData = require('../../assets/data/questions_seed.json');
    const now = Date.now();

    // Converte formato do seed para o formato do app
    return seedData.map((q, index) => ({
      id: q.id,
      type: 'multiple-choice',
      text: q.question,
      alternatives: q.options.map((opt, optIndex) => ({
        id: `${q.id}_opt_${optIndex}`,
        text: opt,
        isCorrect: optIndex === q.correctIndex,
      })),
      category: q.category,
      createdAt: now - (index * 1000), // Ordena por ordem no arquivo
      order: index,
      // Metadados extras
      banca: q.banca,
      ano: q.ano,
      orgao: q.orgao,
      cargo: q.cargo,
      assunto: q.assunto,
    }));
  } catch (error) {
    return [];
  }
};

// Adiciona uma nova questão
export const addQuestion = async (questionData) => {
  try {
    const questions = await loadQuestions();
    const now = Date.now();
    const newQuestion = {
      id: generateId(),
      type: questionData.type || 'multiple-choice', // Tipo da questão
      text: questionData.text,
      alternatives: questionData.alternatives.map((alt, index) => ({
        id: generateId(),
        text: alt.text,
        isCorrect: alt.isCorrect,
      })),
      category: questionData.category,
      subQuestions: questionData.subQuestions || [], // Subquestões
      imageUri: questionData.imageUri || null, // Imagem do enunciado (arquivo local em question_images/)
      createdAt: now,
      order: 0, // Mais recente = menor ordem (aparece primeiro)
    };

    // Atualiza ordem das outras questões
    const updatedQuestions = questions.map((q) => ({
      ...q,
      order: q.order + 1,
    }));

    updatedQuestions.unshift(newQuestion);
    await saveQuestions(updatedQuestions);
    return newQuestion;
  } catch (error) {

    return null;
  }
};

// Atualiza uma questão existente
export const updateQuestion = async (questionId, updates) => {
  try {
    const questions = await loadQuestions();
    const index = questions.findIndex((q) => q.id === questionId);
    if (index === -1) return false;

    questions[index] = {
      ...questions[index],
      ...updates,
    };

    await saveQuestions(questions);
    return true;
  } catch (error) {

    return false;
  }
};

// Remove uma questão
export const deleteQuestion = async (questionId) => {
  try {
    const questions = await loadQuestions();
    const removed = questions.find((q) => q.id === questionId);
    const filtered = questions.filter((q) => q.id !== questionId);
    await saveQuestions(filtered);

    // Remove o arquivo de imagem associado (evita órfãos no documentDirectory)
    if (removed?.imageUri) {
      await deleteQuestionImage(removed.imageUri);
    }

    // Remove também do histórico
    await deleteHistory(questionId);

    return true;
  } catch (error) {

    return false;
  }
};

// Duplica uma questão
export const duplicateQuestionInStorage = async (questionId) => {
  try {
    const questions = await loadQuestions();
    const original = questions.find((q) => q.id === questionId);
    if (!original) return null;

    const now = Date.now();
    const duplicated = {
      ...original,
      id: generateId(),
      createdAt: now,
      order: 0,
      alternatives: original.alternatives.map((alt) => ({
        ...alt,
        id: generateId(),
      })),
      subQuestions: original.subQuestions?.map((sq) => ({
        ...sq,
        id: generateId(),
        alternatives: sq.alternatives.map((alt) => ({
          ...alt,
          id: generateId(),
        })),
      })) || [],
    };

    // Atualiza ordem das outras questões
    const updatedQuestions = questions.map((q) => ({
      ...q,
      order: q.order + 1,
    }));

    updatedQuestions.unshift(duplicated);
    await saveQuestions(updatedQuestions);
    return duplicated;
  } catch (error) {

    return null;
  }
};

// Reordena questões após drag & drop
export const reorderQuestions = async (reorderedQuestions) => {
  try {
    const questionsWithOrder = reorderedQuestions.map((q, index) => ({
      ...q,
      order: index,
    }));
    await saveQuestions(questionsWithOrder);
    return true;
  } catch (error) {

    return false;
  }
};

// ==================== HISTÓRICO ====================

// Carrega todo o histórico
export const loadHistory = async () => {
  try {
    const data = await AsyncStorage.getItem(STORAGE_KEYS.HISTORY);
    return data ? JSON.parse(data) : {};
  } catch (error) {

    return {};
  }
};

// Salva todo o histórico
export const saveHistory = async (history) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
    return true;
  } catch (error) {

    return false;
  }
};

// Obtém histórico de uma questão específica
export const getQuestionHistory = async (questionId) => {
  const history = await loadHistory();
  return history[questionId] || {
    questionId,
    correctCount: 0,
    wrongCount: 0,
    inRepetitionMode: false,
    lastAnswered: null,
  };
};

// Registra uma resposta no histórico
export const recordAnswer = async (questionId, isCorrect) => {
  try {
    const history = await loadHistory();
    const current = history[questionId] || {
      questionId,
      correctCount: 0,
      wrongCount: 0,
      inRepetitionMode: false,
      lastAnswered: null,
    };

    if (isCorrect) {
      current.correctCount += 1;
    } else {
      current.wrongCount += 1;
      current.inRepetitionMode = true;
    }

    current.lastAnswered = Date.now();

    // Se atingiu 50 acertos, remove do modo repetição
    if (current.correctCount >= 50) {
      current.inRepetitionMode = false;
    }

    history[questionId] = current;
    await saveHistory(history);

    // NÃO chama logAnswer aqui: as telas que respondem questões já chamam
    // logAnswer() explicitamente (com category/source corretos). Chamar aqui
    // também causava DUPLA gravação por questão (2 linhas em answer_history +
    // 2× no contador questoes_respondidas/XP). recordAnswer cuida só do
    // histórico por-questão (revisão espaçada); o log/sync é responsabilidade
    // do caller. Único caller que não logava sozinho era a TimedExamScreen —
    // que agora chama logAnswer explicitamente.

    return current;
  } catch (error) {

    return null;
  }
};

// Remove histórico de uma questão
export const deleteHistory = async (questionId) => {
  try {
    const history = await loadHistory();
    delete history[questionId];
    await saveHistory(history);
    return true;
  } catch (error) {

    return false;
  }
};

// Remove questão do modo repetição (após 50 acertos)
export const removeFromRepetitionMode = async (questionId) => {
  try {
    const history = await loadHistory();
    if (history[questionId]) {
      history[questionId].inRepetitionMode = false;
      await saveHistory(history);
    }
    return true;
  } catch (error) {

    return false;
  }
};

// Reseta o modo de repetição de todas as questões
export const resetAllRepetitionMode = async () => {
  try {
    const history = await loadHistory();
    const updatedHistory = {};

    // Percorre todo o histórico e reseta o modo de repetição
    Object.keys(history).forEach((questionId) => {
      updatedHistory[questionId] = {
        ...history[questionId],
        inRepetitionMode: false,
      };
    });

    await saveHistory(updatedHistory);
    return true;
  } catch (error) {

    return false;
  }
};

// Retorna estatísticas de quantas questões estão em revisão
export const getRepetitionStats = async () => {
  try {
    const history = await loadHistory();
    const inRepetition = Object.values(history).filter(
      (h) => h.inRepetitionMode === true
    ).length;
    return {
      total: Object.keys(history).length,
      inRepetition,
    };
  } catch (error) {

    return { total: 0, inRepetition: 0 };
  }
};

// Limpa todos os dados
export const clearAllData = async () => {
  try {
    await AsyncStorage.multiRemove([STORAGE_KEYS.QUESTIONS, STORAGE_KEYS.HISTORY]);
    return true;
  } catch (error) {

    return false;
  }
};

// ==================== LOG DE RESPOSTAS (para estatísticas semanais) ====================

// Carrega o log de respostas
export const loadAnswerLog = async () => {
  try {
    const data = await AsyncStorage.getItem(STORAGE_KEYS.ANSWER_LOG);
    return data ? JSON.parse(data) : [];
  } catch (error) {

    return [];
  }
};

// Salva o log de respostas
export const saveAnswerLog = async (log) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEYS.ANSWER_LOG, JSON.stringify(log));
    return true;
  } catch (error) {

    return false;
  }
};

// Adiciona uma resposta ao log (local + Supabase)
export const logAnswer = async (isCorrect, options = {}) => {
  try {
    const log = await loadAnswerLog();
    log.push({
      timestamp: Date.now(),
      isCorrect,
    });

    // Mantém apenas os últimos 30 dias para não crescer demais
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const filtered = log.filter(entry => entry.timestamp >= thirtyDaysAgo);

    await saveAnswerLog(filtered);

    // Sincronizar com Supabase (não bloqueia)
    syncAnswer(
      options.questionId || `local_${Date.now()}`,
      isCorrect,
      {
        source: options.source || 'user',
        category: options.category || 'GERAL',
        timeSpent: options.timeSpent || null,
      }
    ).catch(() => { });

    return true;
  } catch (error) {

    return false;
  }
};

// Obtém estatísticas da semana atual
export const getWeeklyStats = async () => {
  try {
    const log = await loadAnswerLog();
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

    const weeklyEntries = log.filter(entry => entry.timestamp >= sevenDaysAgo);

    const total = weeklyEntries.length;
    const correct = weeklyEntries.filter(entry => entry.isCorrect).length;
    const wrong = total - correct;
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;

    return {
      total,
      correct,
      wrong,
      percentage,
    };
  } catch (error) {

    return { total: 0, correct: 0, wrong: 0, percentage: 0 };
  }
};

// ==================== CRONOGRAMA DE REVISÕES ====================

// Salva o cronograma de revisões personalizado
export const saveReviewSchedule = async (schedule) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEYS.REVIEW_SCHEDULE, JSON.stringify(schedule));
    return true;
  } catch (error) {

    return false;
  }
};

// Carrega o cronograma de revisões (ou retorna o padrão)
export const loadReviewSchedule = async () => {
  try {
    const data = await AsyncStorage.getItem(STORAGE_KEYS.REVIEW_SCHEDULE);
    if (data) {
      return JSON.parse(data);
    }
    // Retorna configuração padrão se não houver configuração salva
    return DEFAULT_REVIEW_SCHEDULE;
  } catch (error) {

    return DEFAULT_REVIEW_SCHEDULE;
  }
};

// Atualiza as categorias de um dia específico
export const updateDayCategories = async (dayId, categories) => {
  try {
    const schedule = await loadReviewSchedule();
    schedule[dayId] = categories;
    await saveReviewSchedule(schedule);
    return true;
  } catch (error) {

    return false;
  }
};

// ==================== REPETIÇÃO ESPAÇADA ====================

// Utilitário: retorna o início do dia (00:00:00) de uma data
const getStartOfDay = (timestamp) => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

// Utilitário: retorna o fim do dia (23:59:59) de uma data
const getEndOfDay = (timestamp) => {
  const date = new Date(timestamp);
  date.setHours(23, 59, 59, 999);
  return date.getTime();
};

// Utilitário: retorna a data de hoje formatada (YYYY-MM-DD)
const getTodayDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Calcula o intervalo baseado no número de revisões
export const getIntervalForReview = (reviewCount) => {
  // reviewCount 0 -> 3 dias (primeira resolução)
  // reviewCount 1 -> 5 dias
  // reviewCount 2 -> 8 dias
  // reviewCount 3+ -> 12 dias (permanente)
  const intervalIndex = Math.min(reviewCount, SPACED_INTERVALS.length - 1);
  return SPACED_INTERVALS[intervalIndex];
};

// Calcula a próxima data de revisão baseada no número de revisões feitas
export const calculateNextReviewDate = (reviewCount) => {
  const now = new Date();
  const daysToAdd = getIntervalForReview(reviewCount);

  // Próxima data = hoje + intervalo (início do dia, 00:00:00)
  const nextDate = new Date(now);
  nextDate.setDate(nextDate.getDate() + daysToAdd);
  nextDate.setHours(0, 0, 0, 0);

  return nextDate.getTime();
};

// Salva todas as questões espaçadas
export const saveSpacedQuestions = async (questions) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEYS.SPACED_QUESTIONS, JSON.stringify(questions));
    return true;
  } catch (error) {

    return false;
  }
};

// Carrega todas as questões espaçadas
export const loadSpacedQuestions = async () => {
  try {
    const data = await AsyncStorage.getItem(STORAGE_KEYS.SPACED_QUESTIONS);
    return data ? JSON.parse(data) : [];
  } catch (error) {

    return [];
  }
};

// Adiciona uma nova questão espaçada (texto livre)
export const addSpacedQuestion = async (text, answer) => {
  try {
    const questions = await loadSpacedQuestions();
    const now = Date.now();

    // Primeira disponibilidade: após 3 dias (não hoje)
    const firstInterval = SPACED_INTERVALS[0]; // 3 dias
    const nextDate = new Date(now);
    nextDate.setDate(nextDate.getDate() + firstInterval);
    nextDate.setHours(0, 0, 0, 0);

    const newQuestion = {
      id: generateId(),
      originalQuestionId: null,
      text: text.trim(),
      answer: answer.trim(),
      createdAt: now,

      // Controle de revisões
      reviewCount: 0,
      lastReviewedAt: null,
      nextAvailableAt: nextDate.getTime(),
      currentInterval: firstInterval,

      // Status
      isActive: false, // Não ativa imediatamente
      wasReviewedToday: false,
      timesAnsweredToday: 0,

      // Histórico
      reviewHistory: [],
    };

    questions.push(newQuestion);
    await saveSpacedQuestions(questions);
    return newQuestion;
  } catch (error) {

    return null;
  }
};

// Remove uma questão espaçada
export const deleteSpacedQuestion = async (questionId) => {
  try {
    const questions = await loadSpacedQuestions();
    const filtered = questions.filter((q) => q.id !== questionId);
    await saveSpacedQuestions(filtered);
    return true;
  } catch (error) {

    return false;
  }
};

// Retorna questões disponíveis para revisão HOJE (ativas)
export const getTodaySpacedQuestions = async () => {
  try {
    const questions = await loadSpacedQuestions();
    const now = Date.now();
    const todayStart = getStartOfDay(now);

    // Filtra questões que estão ativas OU cuja data já passou (não foram revisadas)
    const availableQuestions = questions.filter((q) => {
      return q.isActive || q.nextAvailableAt <= todayStart;
    });

    return availableQuestions;
  } catch (error) {

    return [];
  }
};

// Retorna questões aguardando próxima revisão
export const getUpcomingSpacedQuestions = async () => {
  try {
    const questions = await loadSpacedQuestions();
    const now = Date.now();
    const todayStart = getStartOfDay(now);

    // Filtra questões que:
    // 1. Já têm data futura e não estão ativas (comportamento original)
    // 2. OU foram revisadas hoje (nova feature: mostrar imediatamente após revisar)
    const upcomingQuestions = questions.filter((q) => {
      const isFutureAndInactive = !q.isActive && q.nextAvailableAt > todayStart;
      const wasReviewedToday = q.wasReviewedToday === true;
      return isFutureAndInactive || wasReviewedToday;
    });

    // Para questões revisadas hoje, calcula a próxima data virtual
    const questionsWithNextDate = upcomingQuestions.map((q) => {
      if (q.wasReviewedToday) {
        // Calcula a próxima data baseada no reviewCount + 1 (após esta revisão)
        const nextReviewCount = q.reviewCount + 1;
        const daysToAdd = getIntervalForReview(nextReviewCount);
        const nextDate = new Date(now);
        nextDate.setDate(nextDate.getDate() + daysToAdd);
        nextDate.setHours(0, 0, 0, 0);

        return {
          ...q,
          nextAvailableAt: nextDate.getTime(),
          currentInterval: daysToAdd,
          // Flag para indicar que é uma projeção (revisada hoje)
          isProjectedDate: true,
        };
      }
      return q;
    });

    // Ordena por data mais próxima
    questionsWithNextDate.sort((a, b) => a.nextAvailableAt - b.nextAvailableAt);

    return questionsWithNextDate;
  } catch (error) {

    return [];
  }
};

// Marca uma questão como respondida (permite múltiplas no mesmo dia)
export const markSpacedQuestionAnswered = async (questionId) => {
  try {
    const questions = await loadSpacedQuestions();
    const index = questions.findIndex((q) => q.id === questionId);

    if (index === -1) return null;

    const question = questions[index];
    const now = Date.now();

    // Incrementa contador de respostas do dia
    question.timesAnsweredToday = (question.timesAnsweredToday || 0) + 1;

    // Se é a primeira resposta do dia, marca como revisada
    if (!question.wasReviewedToday) {
      question.wasReviewedToday = true;
      question.lastReviewedAt = now;

      // Adiciona ao histórico (apenas uma entrada por dia)
      question.reviewHistory.push({
        reviewedAt: now,
        reviewNumber: question.reviewCount + 1,
      });
    }

    questions[index] = question;
    await saveSpacedQuestions(questions);

    return question;
  } catch (error) {

    return null;
  }
};

// Reset diário - Executar quando um novo dia começa
export const performDailyReset = async () => {
  try {
    const questions = await loadSpacedQuestions();
    const now = Date.now();
    const todayStart = getStartOfDay(now);

    let updated = false;

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];

      // Se foi revisada hoje (ontem à noite), calcular próxima data
      if (q.wasReviewedToday) {
        q.reviewCount += 1;
        q.currentInterval = getIntervalForReview(q.reviewCount);
        q.nextAvailableAt = calculateNextReviewDate(q.reviewCount);
        q.isActive = false;
        q.wasReviewedToday = false;
        q.timesAnsweredToday = 0;
        updated = true;
      }

      // Ativar questões cuja data chegou
      if (!q.isActive && q.nextAvailableAt <= todayStart) {
        q.isActive = true;
        updated = true;
      }
    }

    if (updated) {
      await saveSpacedQuestions(questions);
    }

    // Salvar data do último reset
    await AsyncStorage.setItem(STORAGE_KEYS.SPACED_LAST_RESET, getTodayDateString());

    return true;
  } catch (error) {

    return false;
  }
};

// Verifica se precisa fazer reset diário (ao abrir o app)
export const checkAndPerformDailyReset = async () => {
  try {
    const lastReset = await AsyncStorage.getItem(STORAGE_KEYS.SPACED_LAST_RESET);
    const today = getTodayDateString();

    if (lastReset !== today) {
      await performDailyReset();
      return true;
    }
    return false;
  } catch (error) {

    return false;
  }
};

// Retorna estatísticas das questões espaçadas
export const getSpacedStats = async () => {
  try {
    const questions = await loadSpacedQuestions();
    const todayQuestions = await getTodaySpacedQuestions();
    const reviewedToday = questions.filter(q => q.wasReviewedToday).length;

    return {
      total: questions.length,
      todayCount: todayQuestions.length,
      reviewedToday,
      availableNow: todayQuestions.filter(q => !q.wasReviewedToday).length,
    };
  } catch (error) {

    return { total: 0, todayCount: 0, reviewedToday: 0, availableNow: 0 };
  }
};

// Retorna questões normais criadas há mais de X dias (para sugestão de repetição)
export const getOldQuestionsForSpacedRepetition = async (daysOld = 4) => {
  try {
    const allQuestions = await loadQuestions();
    const spacedQuestions = await loadSpacedQuestions();

    // IDs das questões já no sistema de repetição espaçada
    const spacedIds = new Set(spacedQuestions.map(q => q.originalQuestionId).filter(Boolean));

    const cutoffDate = Date.now() - (daysOld * 24 * 60 * 60 * 1000);

    // Filtra questões criadas há mais de X dias e que não estão no sistema
    const oldQuestions = allQuestions.filter(q => {
      return q.createdAt <= cutoffDate && !spacedIds.has(q.id);
    });

    return oldQuestions;
  } catch (error) {

    return [];
  }
};

// Adiciona uma questão existente ao sistema de repetição espaçada
export const addExistingQuestionToSpaced = async (originalQuestion) => {
  try {
    const questions = await loadSpacedQuestions();

    // Dedup: se a questão já está no sistema espaçado, não duplica (retorna a existente).
    if (originalQuestion.id) {
      const existing = questions.find(
        (q) => q.originalQuestionId === originalQuestion.id
      );
      if (existing) {
        return existing;
      }
    }

    const now = Date.now();

    // Primeira disponibilidade: após 3 dias
    const firstInterval = SPACED_INTERVALS[0];
    const nextDate = new Date(now);
    nextDate.setDate(nextDate.getDate() + firstInterval);
    nextDate.setHours(0, 0, 0, 0);

    // Extrai resposta das alternativas corretas
    const answer = originalQuestion.alternatives
      ? originalQuestion.alternatives
        .filter(a => a.isCorrect)
        .map(a => a.text)
        .join(', ') || 'Ver alternativa correta'
      : 'Sem resposta definida';

    const newSpacedQuestion = {
      id: generateId(),
      originalQuestionId: originalQuestion.id,
      text: originalQuestion.text,
      answer,
      // Conteúdo completo p/ a revisão funcionar sem depender só do banco local
      category: originalQuestion.category || 'GERAL',
      type: originalQuestion.type || 'multiple-choice',
      alternatives: originalQuestion.alternatives || [],
      createdAt: now,

      // Controle de revisões
      reviewCount: 0,
      lastReviewedAt: null,
      nextAvailableAt: nextDate.getTime(),
      currentInterval: firstInterval,

      // Status
      isActive: false,
      wasReviewedToday: false,
      timesAnsweredToday: 0,

      // Histórico
      reviewHistory: [],
    };

    questions.push(newSpacedQuestion);
    await saveSpacedQuestions(questions);
    return newSpacedQuestion;
  } catch (error) {

    return null;
  }
};

// ==================== ESTATÍSTICAS DE DESEMPENHO ====================

// Helper: Retorna timestamp do início do período
const getPeriodStartDate = (period) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  switch (period) {
    case 'today':
      return now.getTime();
    case '7days':
      now.setDate(now.getDate() - 7);
      return now.getTime();
    case '15days':
      now.setDate(now.getDate() - 15);
      return now.getTime();
    case '30days':
      now.setDate(now.getDate() - 30);
      return now.getTime();
    case 'all':
    default:
      return 0; // Desde sempre
  }
};

// Estatísticas gerais de desempenho
export const getPerformanceStats = async (period = '30days', logOverride = null) => {
  try {
    // logOverride: entradas já normalizadas (ex.: vindas do banco) no shape
    // { timestamp, isCorrect }. Quando ausente, usa o answer log local.
    const log = logOverride ?? await loadAnswerLog();
    const startDate = getPeriodStartDate(period);

    const filteredLog = log.filter(entry => entry.timestamp >= startDate);

    const total = filteredLog.length;
    const correct = filteredLog.filter(entry => entry.isCorrect).length;
    const wrong = total - correct;
    const accuracyPercentage = total > 0 ? Math.round((correct / total) * 100 * 100) / 100 : 0;

    return {
      totalQuestions: total,
      correctAnswers: correct,
      wrongAnswers: wrong,
      accuracyPercentage,
    };
  } catch (error) {

    return { totalQuestions: 0, correctAnswers: 0, wrongAnswers: 0, accuracyPercentage: 0 };
  }
};

// Desempenho por categoria/disciplina (baseado no histórico de questões)
export const getSubjectsPerformance = async (period = '30days', logOverride = null) => {
  try {
    // logOverride: array de entradas { timestamp, isCorrect, category } (ex.: do
    // banco). Local: deriva do HISTORY (mapa por questionId → array de valores).
    const entries = logOverride ?? Object.values(await loadHistory());
    const startDate = getPeriodStartDate(period);

    // Agrupa por categoria
    const categoryStats = {};

    entries.forEach(entry => {
      if (entry.timestamp >= startDate && entry.category) {
        if (!categoryStats[entry.category]) {
          categoryStats[entry.category] = { total: 0, correct: 0 };
        }
        categoryStats[entry.category].total++;
        if (entry.isCorrect) {
          categoryStats[entry.category].correct++;
        }
      }
    });

    // Converte para array com percentuais
    const subjects = Object.entries(categoryStats).map(([name, stats]) => ({
      id: name,
      name,
      totalQuestions: stats.total,
      correctAnswers: stats.correct,
      percentage: stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0,
    }));

    // Ordena por percentual decrescente
    subjects.sort((a, b) => b.percentage - a.percentage);

    return subjects;
  } catch (error) {

    return [];
  }
};

// Histórico diário para gráfico de linhas (ou por hora quando period='today')
export const getHistoricalData = async (period = '30days', logOverride = null) => {
  try {
    const log = logOverride ?? await loadAnswerLog();
    const startDate = getPeriodStartDate(period);

    const filteredLog = log.filter(entry => entry.timestamp >= startDate);

    // Se for 'today', agrupa por HORA (últimas 10 horas)
    if (period === 'today') {
      const now = new Date();
      const currentHour = now.getHours();

      // Cria array com as últimas 10 horas
      const hourlyData = [];
      for (let i = 9; i >= 0; i--) {
        let hour = currentHour - i;
        let displayHour = hour;

        // Ajusta para horas negativas (dia anterior)
        if (hour < 0) {
          hour = 24 + hour;
          displayHour = hour;
        }

        const hourKey = String(hour).padStart(2, '0');
        const displayLabel = `${String(displayHour).padStart(2, '0')}h`;

        hourlyData.push({
          date: displayLabel,
          hour: hour,
          correctAnswers: 0,
          wrongAnswers: 0
        });
      }

      // Conta respostas por hora de hoje
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);

      filteredLog.forEach(entry => {
        const entryDate = new Date(entry.timestamp);

        // Apenas entradas de hoje
        if (entryDate >= todayStart) {
          const entryHour = entryDate.getHours();

          // Encontra o slot correspondente
          const slot = hourlyData.find(h => h.hour === entryHour);
          if (slot) {
            if (entry.isCorrect) {
              slot.correctAnswers++;
            } else {
              slot.wrongAnswers++;
            }
          }
        }
      });

      return hourlyData;
    }

    // Se for '7days', agrupa por dia da semana (Seg, Ter, Qua...)
    if (period === '7days') {
      const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
      const now = new Date();

      // Cria array com os últimos 7 dias
      const weeklyData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);

        const dayOfWeek = date.getDay();
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        weeklyData.push({
          date: weekDays[dayOfWeek],
          dateKey: dateKey,
          timestamp: date.getTime(),
          correctAnswers: 0,
          wrongAnswers: 0
        });
      }

      // Conta respostas por dia
      filteredLog.forEach(entry => {
        const entryDate = new Date(entry.timestamp);
        const entryDateKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`;

        const slot = weeklyData.find(d => d.dateKey === entryDateKey);
        if (slot) {
          if (entry.isCorrect) {
            slot.correctAnswers++;
          } else {
            slot.wrongAnswers++;
          }
        }
      });

      return weeklyData;
    }

    // Se for '15days' (agora exibido como 10 dias), mostra os últimos 10 dias com datas
    if (period === '15days') {
      const now = new Date();

      // Cria array com os últimos 10 dias
      const tenDaysData = [];
      for (let i = 9; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const dateKey = `${date.getFullYear()}-${month}-${day}`;

        tenDaysData.push({
          date: `${day}/${month}`, // Formato: DD/MM
          dateKey: dateKey,
          timestamp: date.getTime(),
          correctAnswers: 0,
          wrongAnswers: 0
        });
      }

      // Conta respostas por dia
      filteredLog.forEach(entry => {
        const entryDate = new Date(entry.timestamp);
        const entryDateKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`;

        const slot = tenDaysData.find(d => d.dateKey === entryDateKey);
        if (slot) {
          if (entry.isCorrect) {
            slot.correctAnswers++;
          } else {
            slot.wrongAnswers++;
          }
        }
      });

      return tenDaysData;
    }

    // Se for '30days', agrupa por 4 semanas com intervalo de datas
    if (period === '30days') {
      const now = new Date();
      now.setHours(23, 59, 59, 999);

      // Cria array com as 4 últimas semanas
      const weeksData = [];
      for (let week = 3; week >= 0; week--) {
        const weekEnd = new Date(now);
        weekEnd.setDate(weekEnd.getDate() - (week * 7));
        weekEnd.setHours(23, 59, 59, 999);

        const weekStart = new Date(weekEnd);
        weekStart.setDate(weekStart.getDate() - 6);
        weekStart.setHours(0, 0, 0, 0);

        const startDay = String(weekStart.getDate()).padStart(2, '0');
        const startMonth = String(weekStart.getMonth() + 1).padStart(2, '0');
        const endDay = String(weekEnd.getDate()).padStart(2, '0');
        const endMonth = String(weekEnd.getMonth() + 1).padStart(2, '0');

        weeksData.push({
          date: `${startDay}-${endDay}/${endMonth}`, // Formato: DD-DD/MM
          weekStart: weekStart.getTime(),
          weekEnd: weekEnd.getTime(),
          correctAnswers: 0,
          wrongAnswers: 0
        });
      }

      // Conta respostas por semana
      filteredLog.forEach(entry => {
        const entryTime = entry.timestamp;

        const slot = weeksData.find(w => entryTime >= w.weekStart && entryTime <= w.weekEnd);
        if (slot) {
          if (entry.isCorrect) {
            slot.correctAnswers++;
          } else {
            slot.wrongAnswers++;
          }
        }
      });

      return weeksData;
    }

    // Para outros períodos, agrupa por dia (comportamento original)
    const dailyData = {};

    filteredLog.forEach(entry => {
      const date = new Date(entry.timestamp);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      if (!dailyData[dateKey]) {
        dailyData[dateKey] = { date: dateKey, correctAnswers: 0, wrongAnswers: 0 };
      }

      if (entry.isCorrect) {
        dailyData[dateKey].correctAnswers++;
      } else {
        dailyData[dateKey].wrongAnswers++;
      }
    });

    // Converte para array e ordena por data
    const result = Object.values(dailyData).sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return result;
  } catch (error) {

    return [];
  }
};

// Distribuição por dificuldade (simplificada - baseada em acertos)
export const getDifficultyDistribution = async (period = '30days', logOverride = null) => {
  try {
    const log = logOverride ?? await loadAnswerLog();
    const startDate = getPeriodStartDate(period);

    const filteredLog = log.filter(entry => entry.timestamp >= startDate);
    const total = filteredLog.length;

    if (total === 0) {
      return [
        { level: 'Muito Fácil', count: 0, percentage: 0 },
        { level: 'Fácil', count: 0, percentage: 0 },
        { level: 'Médio', count: 0, percentage: 0 },
        { level: 'Difícil', count: 0, percentage: 0 },
        { level: 'Muito Difícil', count: 0, percentage: 0 },
      ];
    }

    // Como não temos dificuldade nas questões, usamos distribuição simulada
    // baseada na taxa de acertos do período
    const correct = filteredLog.filter(e => e.isCorrect).length;
    const rate = correct / total;

    // Distribui baseado na taxa de acertos
    const veryEasy = Math.round(total * rate * 0.35);
    const easy = Math.round(total * rate * 0.35);
    const medium = Math.round(total * 0.2);
    const hard = Math.round(total * (1 - rate) * 0.35);
    const veryHard = total - veryEasy - easy - medium - hard;

    return [
      { level: 'Muito Fácil', count: veryEasy, percentage: Math.round((veryEasy / total) * 100) },
      { level: 'Fácil', count: easy, percentage: Math.round((easy / total) * 100) },
      { level: 'Médio', count: medium, percentage: Math.round((medium / total) * 100) },
      { level: 'Difícil', count: hard, percentage: Math.round((hard / total) * 100) },
      { level: 'Muito Difícil', count: Math.max(0, veryHard), percentage: Math.round((Math.max(0, veryHard) / total) * 100) },
    ];
  } catch (error) {

    return [];
  }
};

